import ExportableSection from './ExportableSection.jsx'
import { PULSE_COLORS } from './pulseConfig.js'

function formatCount(value, suffix = '') {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '-'
    return `${new Intl.NumberFormat('en-US').format(value)}${suffix}`
}

function coverageStatus(value) {
    if (value >= 92) return { label: 'Exceptional', color: '#34D399', bg: '#064E3B' }
    if (value >= 88) return { label: 'Excellent', color: '#2DD4BF', bg: '#134E4A' }
    if (value >= 85) return { label: 'Strong', color: '#60A5FA', bg: '#1E3A8A' }
    return { label: 'Healthy', color: '#FBBF24', bg: '#78350F' }
}

export default function AutomationCoverageInsights({
    data,
    loading,
    headerAction,
    automationSummary,
    compact = false,
}) {
    const portals = data?.portals ?? []
    const topPortals = portals.slice(0, 6)
    const topDistribution = (data?.distribution ?? []).slice(0, 4)
    const mixGridColumns = compact ? '1fr' : 'repeat(auto-fit, minmax(170px, 1fr))'

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px', margin: 0 }}>
                        Coverage Snapshot
                    </h3>
                    {headerAction}
                </div>
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: '6px 0 0' }}>
                    Semantic coverage snapshot from e2e origin/main.
                </p>
            </div>

            <div style={{ display: 'grid', gap: '20px' }}>
                {automationSummary}

                <ExportableSection filename="pulse-portal-coverage-strength">
                    {({ captureRef, exportButton }) => (
                        <div
                            ref={captureRef}
                            style={{
                                paddingTop: automationSummary ? '20px' : 0,
                                borderTop: automationSummary ? `1px solid ${PULSE_COLORS.border}` : 'none',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '12px',
                                    marginBottom: '12px',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <p
                                        style={{
                                            color: PULSE_COLORS.textMuted,
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            margin: 0,
                                        }}
                                    >
                                        Portal Coverage Strength
                                    </p>
                                    {exportButton}
                                </div>
                                {data?.sourceUrl && (
                                    <a
                                        href={data.sourceUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            color: '#60A5FA',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            textDecoration: 'none',
                                        }}
                                    >
                                        Source: {data.ref}
                                    </a>
                                )}
                            </div>

                            {loading && topPortals.length === 0 ? (
                                <LoadingRows count={4} height={48} />
                            ) : (
                                <div style={{ display: 'grid', gap: '12px' }}>
                                    {topPortals.map((portal) => {
                                        const status = coverageStatus(portal.coverageMidpoint)
                                        const widthPct = Math.min(100, Math.max(8, portal.coverageMidpoint))
                                        return (
                                            <div key={portal.name}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '12px',
                                                    }}
                                                >
                                                    <div style={{ minWidth: 0 }}>
                                                        <p
                                                            style={{
                                                                color: PULSE_COLORS.text,
                                                                fontSize: '13px',
                                                                fontWeight: 700,
                                                                margin: 0,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {portal.name}
                                                        </p>
                                                        <p
                                                            style={{
                                                                color: PULSE_COLORS.textMuted,
                                                                fontSize: '12px',
                                                                margin: '2px 0 0',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {portal.keyStrengths}
                                                        </p>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span
                                                            style={{
                                                                padding: '2px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '11px',
                                                                fontWeight: 700,
                                                                color: status.color,
                                                                background: status.bg,
                                                            }}
                                                        >
                                                            {status.label}
                                                        </span>
                                                        <span
                                                            style={{
                                                                width: '64px',
                                                                textAlign: 'right',
                                                                color: PULSE_COLORS.text,
                                                                fontSize: '13px',
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {portal.coverageLabel}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div
                                                    style={{
                                                        height: '8px',
                                                        marginTop: '6px',
                                                        borderRadius: '999px',
                                                        background: PULSE_COLORS.bgInner,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: `${widthPct}%`,
                                                            height: '100%',
                                                            borderRadius: '999px',
                                                            background: status.color,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </ExportableSection>
            </div>

            <ExportableSection filename="pulse-automation-mix">
                {({ captureRef, exportButton }) => (
                    <div
                        ref={captureRef}
                        style={{ marginTop: '20px', paddingTop: '20px', borderTop: `1px solid ${PULSE_COLORS.border}` }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px',
                                marginBottom: '12px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <p
                                    style={{
                                        color: PULSE_COLORS.textMuted,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        margin: 0,
                                    }}
                                >
                                    Automation Mix
                                </p>
                                {exportButton}
                            </div>
                            <span style={{ color: PULSE_COLORS.success, fontSize: '12px', fontWeight: 700 }}>
                                {formatCount(data?.totalTestCases ?? null, '+')} automated scenarios across core suites
                            </span>
                        </div>

                        {loading && topDistribution.length === 0 ? (
                            <LoadingRows count={4} height={128} gridColumns={mixGridColumns} />
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: mixGridColumns, gap: '12px' }}>
                                {topDistribution.map((row) => (
                                    <div
                                        key={row.category}
                                        style={{
                                            borderRadius: '8px',
                                            border: '1px solid #065F46',
                                            background: '#064E3B33',
                                            padding: '16px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: '12px',
                                            }}
                                        >
                                            <p
                                                style={{
                                                    color: PULSE_COLORS.text,
                                                    fontSize: '13px',
                                                    fontWeight: 700,
                                                    margin: 0,
                                                }}
                                            >
                                                {row.category}
                                            </p>
                                            {typeof row.percentage === 'number' && Number.isFinite(row.percentage) && (
                                                <span
                                                    style={{
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        background: PULSE_COLORS.bgInner,
                                                        color: PULSE_COLORS.textMuted,
                                                        fontSize: '12px',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {row.percentage}%
                                                </span>
                                            )}
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                justifyContent: 'space-between',
                                                gap: '12px',
                                                marginTop: '12px',
                                            }}
                                        >
                                            <div>
                                                <div
                                                    style={{
                                                        color: PULSE_COLORS.success,
                                                        fontSize: '30px',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {formatCount(row.testCount)}
                                                </div>
                                                <p
                                                    style={{
                                                        color: '#6EE7B7',
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        textTransform: 'uppercase',
                                                        margin: 0,
                                                    }}
                                                >
                                                    Tests
                                                </p>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div
                                                    style={{
                                                        color: PULSE_COLORS.text,
                                                        fontSize: '18px',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {formatCount(row.fileCount)}
                                                </div>
                                                <p
                                                    style={{
                                                        color: PULSE_COLORS.textMuted,
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        textTransform: 'uppercase',
                                                        margin: 0,
                                                    }}
                                                >
                                                    Files
                                                </p>
                                            </div>
                                        </div>
                                        <p
                                            style={{
                                                color: PULSE_COLORS.textMuted,
                                                fontSize: '12px',
                                                lineHeight: 1.4,
                                                margin: '12px 0 0',
                                            }}
                                        >
                                            {row.coverageFocus}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </ExportableSection>
        </div>
    )
}

export function CoverageWinsBulletin({ wins = [], loading }) {
    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #1E3A8A',
            }}
        >
            <div style={{ marginBottom: '16px' }}>
                <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px', margin: 0 }}>
                    Latest Changes
                </h3>
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: '6px 0 0' }}>
                    Recently merged e2e automation work from GitLab.
                </p>
            </div>

            {loading && wins.length === 0 ? (
                <LoadingRows count={7} height={56} />
            ) : wins.length === 0 ? (
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: 0 }}>
                    No recent coverage wins found.
                </p>
            ) : (
                <div style={{ display: 'grid', gap: '8px' }}>
                    {wins.slice(0, 7).map((win) => (
                        <a
                            key={`${win.title}-${win.url ?? ''}`}
                            href={win.url ?? undefined}
                            target={win.url ? '_blank' : undefined}
                            rel={win.url ? 'noreferrer' : undefined}
                            style={{
                                display: 'block',
                                borderRadius: '8px',
                                border: '1px solid #1E3A8A',
                                background: '#1E3A8A33',
                                padding: '8px 12px',
                                textDecoration: 'none',
                            }}
                        >
                            <p
                                style={{
                                    color: PULSE_COLORS.text,
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    margin: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {win.title}
                            </p>
                            {win.coverageArea && (
                                <p
                                    style={{
                                        color: PULSE_COLORS.success,
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        margin: '4px 0 0',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {win.coverageArea}
                                </p>
                            )}
                        </a>
                    ))}
                </div>
            )}
        </div>
    )
}

function LoadingRows({ count, height, gridColumns }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: gridColumns ?? '1fr', gap: '10px' }}>
            {[...Array(count)].map((_, i) => (
                <div key={i} style={{ height: `${height}px`, borderRadius: '8px', background: PULSE_COLORS.border }} />
            ))}
        </div>
    )
}
